import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  Edit3,
  Trash2,
  Search,
  MapPin,
  Star,
  AlertCircle,
  Home,
} from "lucide-react";
import AddressFormModal from "./AddressFormModal";
import addressService from "@/services/addressService";
import { showError, showInfo } from "@/store/errorStore";

/**
 * Componente de gerenciamento de endereços
 * CRUD completo com listagem, criação, edição e exclusão
 */
export default function AddressesManagement() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [savingAddress, setSavingAddress] = useState(false);
  const [deletingAddressId, setDeletingAddressId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  /**
   * Carregar endereços e usuários
   */
  const loadData = async () => {
    setLoading(true);
    try {
      const addressesData = await addressService.getAllAddresses();
      setAddresses(Array.isArray(addressesData) ? addressesData : []);
    } catch (error) {
      showError(
        error.response?.data?.message || "Erro ao carregar dados",
        "Erro de Carregamento"
      );
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Buscar todos os endereços
   */
  const fetchAddresses = async () => {
    try {
      const data = await addressService.getAllAddresses();
      setAddresses(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(
        error.response?.data?.message || "Erro ao carregar endereços",
        "Erro de Carregamento"
      );
      setAddresses([]);
    }
  };

  /**
   * Abrir modal para criar novo endereço
   */
  const handleCreateAddress = () => {
    setEditingAddress(null);
    setShowModal(true);
  };

  /**
   * Abrir modal para editar endereço
   */
  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setShowModal(true);
  };

  /**
   * Salvar endereço (criar ou atualizar)
   */
  const handleSaveAddress = async (addressData) => {
    setSavingAddress(true);
    try {
      if (editingAddress) {
        // Atualizar endereço existente
        await addressService.updateAddress(editingAddress.id, addressData);
        showInfo("Endereço atualizado com sucesso", "Endereço Atualizado");
      } else {
        // Criar novo endereço
        await addressService.createAddress(addressData);
        showInfo("Endereço criado com sucesso", "Endereço Criado");
      }

      setShowModal(false);
      setEditingAddress(null);
      await fetchAddresses();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erro ao salvar endereço";
      showError(errorMessage, "Erro");
    } finally {
      setSavingAddress(false);
    }
  };

  /**
   * Definir endereço como padrão
   */
  const handleSetAsDefault = async (address) => {
    try {
      await addressService.setAsDefault(address.id);
      showInfo(
        `Endereço "${address.street}, ${address.number}" definido como padrão`,
        "Endereço Padrão"
      );
      await fetchAddresses();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erro ao definir endereço padrão";
      showError(errorMessage, "Erro");
    }
  };

  /**
   * Confirmar exclusão de endereço
   */
  const handleDeleteClick = (address) => {
    setDeletingAddressId(address.id);
    setShowDeleteDialog(true);
  };

  /**
   * Executar exclusão de endereço
   */
  const handleConfirmDelete = async () => {
    if (!deletingAddressId) return;

    try {
      await addressService.deleteAddress(deletingAddressId);
      showInfo("Endereço removido com sucesso", "Endereço Deletado");
      await fetchAddresses();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erro ao deletar endereço";
      showError(errorMessage, "Erro");
    } finally {
      setShowDeleteDialog(false);
      setDeletingAddressId(null);
    }
  };

  /**
   * Filtrar endereços por busca
   */
  const filteredAddresses = addresses.filter((address) => {
    const query = searchQuery.toLowerCase();
    const logradouro = (address.logradouro || "").toLowerCase();
    const localidade = (address.localidade || "").toLowerCase();
    const bairro = (address.bairro || "").toLowerCase();
    const uf = (address.uf || "").toLowerCase();
    const cep = (address.cep || "").toLowerCase();

    return (
      logradouro.includes(query) ||
      localidade.includes(query) ||
      bairro.includes(query) ||
      uf.includes(query) ||
      cep.includes(query)
    );
  });

  /**
   * Formatar endereço completo em uma linha
   */
  const formatFullAddress = (address) => {
    const parts = [
      address.logradouro,
      address.complemento,
      address.bairro,
      address.localidade,
      address.estado,
      address.uf,
    ].filter(Boolean);

    return parts.join(", ");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Gerenciamento de Endereços
          </h1>
          <p className="text-gray-500 mt-1">
            {filteredAddresses.length}{" "}
            {filteredAddresses.length === 1 ? "endereço" : "endereços"}{" "}
            cadastrados
          </p>
        </div>

        <Button
          onClick={handleCreateAddress}
          style={{ background: "#c9332C" }}
          onMouseEnter={(e) => (e.target.style.background = "#a02820")}
          onMouseLeave={(e) => (e.target.style.background = "#c9332C")}
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Endereço
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>Buscar Endereços</CardTitle>
          <CardDescription>
            Pesquise por rua, cidade, bairro ou usuário
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Digite para buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Addresses Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Endereços</CardTitle>
          <CardDescription>
            Gerencie os endereços cadastrados no sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando endereços...</p>
              </div>
            </div>
          ) : filteredAddresses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MapPin className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-gray-600 font-medium">
                {searchQuery
                  ? "Nenhum endereço encontrado"
                  : "Nenhum endereço cadastrado"}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {searchQuery
                  ? "Tente outro termo de busca"
                  : "Clique em 'Novo Endereço' para começar"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Endereço</TableHead>
                    <TableHead>Bairro</TableHead>
                    <TableHead>Cidade/UF</TableHead>
                    <TableHead>CEP</TableHead>
                    <TableHead>Coordenadas</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAddresses.map((address) => (
                    <TableRow key={address.id}>
                      <TableCell className="font-mono text-sm text-gray-500">
                        #{address.id}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div
                          className="truncate"
                          title={formatFullAddress(address)}
                        >
                          {address.logradouro || "-"}
                          {address.complemento && ` - ${address.complemento}`}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {address.bairro || "-"}
                      </TableCell>
                      <TableCell>
                        {address.localidade || "-"}/{address.uf || "-"}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {address.cep
                          ? address.cep.replace(/(\d{5})(\d{3})/, "$1-$2")
                          : "-"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {address.latitude && address.longitude ? (
                          <div>
                            <div>Lat: {address.latitude.toFixed(6)}</div>
                            <div>Lng: {address.longitude.toFixed(6)}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {address.principal ? (
                          <Badge
                            variant="outline"
                            className="bg-green-100 text-green-800 border-green-200"
                          >
                            <Star className="w-3 h-3 mr-1 fill-current" />
                            Padrão
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-600">
                            <Home className="w-3 h-3 mr-1" />
                            Normal
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {!address.principal && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSetAsDefault(address)}
                              title="Definir como padrão"
                            >
                              <Star className="w-4 h-4 mr-1" />
                              Padrão
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditAddress(address)}
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteClick(address)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Excluir
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Address Form Modal */}
      <AddressFormModal
        open={showModal}
        onOpenChange={setShowModal}
        address={editingAddress}
        onSave={handleSaveAddress}
        loading={savingAddress}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este endereço? Esta ação não pode
              ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingAddressId(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
